import {DataEditorRef, Item, Item as GridItem} from "@glideapps/glide-data-grid";
import {DispatchEntity, ShiftEntity} from "@ots-frontend/ots-data";
import {forwardRef, useCallback, useEffect, useState} from "react";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {useLayer} from "react-laag";
import {Menu} from "@mantine/core";
import {IconCopy, IconEdit, IconEye} from "@tabler/icons-react";
import {Theme} from "@glideapps/glide-data-grid/dist/ts/common/styles";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {EntityDataGridProps} from "../EntityDataGrid/EntityDataGrid";
import {EMPTY_ARRAY, useShiftDataGrid} from "./useShiftDataGrid";
import {GridCellContextMenu, GridRowContextMenu, isSizedGridColumn} from "../DataGrid/useDataGrid";
import {HasAccess} from "../../auth/HasAccess/HasAccess";
import {ThemedDataGrid} from "../DataGrid/ThemedDataGrid";

export type ShiftDataGridProps = EntityDataGridProps<ShiftEntity> & {
  onItemClick?: (item: ShiftDataGridContextMenuItem, data: ShiftEntity) => void;
}

export const ShiftDataGrid = forwardRef<DataEditorRef, ShiftDataGridProps>((props, ref) => {
  const {
    data = EMPTY_ARRAY,
    gridSelection,
    onItemClick,
    onCellClicked,
    colWidths,
    onColumnResize,
    sort,
    onSort,
    ...rest
  } = props;

  const [contextMenu, setContextMenu] = useState<GridRowContextMenu<ShiftEntity>>();

  const dataGridArgs = useShiftDataGrid({
    data: data,
    colWidths: colWidths,
    sort: sort,
  });

  const {
    hoverRow,
    getOriginalIndex
  } = dataGridArgs;

  useEffect(() => {
    if (onColumnResize) {
      const widths = dataGridArgs.columns.map((c) => {
        if (isSizedGridColumn(c)) {
          return c.width
        } else {
          return undefined
        }
      })
      onColumnResize(widths)
    }
  }, [dataGridArgs.columns, onColumnResize]);

  const isOpen = typeof contextMenu !== "undefined";

  const {layerProps, renderLayer} = useLayer({
    isOpen: isOpen,
    auto: true,
    placement: "bottom-end",
    triggerOffset: 2,
    onOutsideClick: () => setContextMenu(undefined),
    trigger: {
      getBounds: () => ({
        left: contextMenu?.bounds.x ?? 0,
        top: contextMenu?.bounds.y ?? 0,
        width: contextMenu?.bounds.width ?? 0,
        height: contextMenu?.bounds.height ?? 0,
        right: (contextMenu?.bounds.x ?? 0) + (contextMenu?.bounds.width ?? 0),
        bottom: (contextMenu?.bounds.y ?? 0) + (contextMenu?.bounds.height ?? 0)
      })
    }
  });

  const onCellContextMenu = useCallback((cell: GridItem, event: CellClickedEventArgs) => {
    event.preventDefault();

    const rootRow = getOriginalIndex(cell[1]);

    if (gridSelection && gridSelection.rows.length > 0) {
      setContextMenu({
        col: cell[0],
        row: rootRow,
        bounds: event.bounds,
        data: gridSelection.rows.toArray().map((row) => data[getOriginalIndex(row)])
      });
    } else {
      setContextMenu({
        col: cell[0],
        row: rootRow,
        bounds: event.bounds,
        data: [data[rootRow]]
      });
    }
  }, [data, getOriginalIndex, gridSelection]);

  const onContextMenuItemClick = useCallback((item: ShiftDataGridContextMenuItem, data: ShiftEntity) => {
    if (onItemClick) {
      onItemClick(item, data);
    }
  }, [onItemClick]);

  const getRowThemeOverride = useCallback((row: number): Partial<Theme> | undefined => {
    const shift = data[getOriginalIndex(row)];

    if (shift) {
      if (!shift.endDttm) {
        return {
          bgCell: hoverRow === row ? "#acb696" : "#d8e4bc"
        }
      }
    }

    if (hoverRow === row) {
      return {
        bgCell: hoverRow === row ? "#f0f0f0" : undefined
      };
    }

    return undefined;
  }, [data, hoverRow, getOriginalIndex]);

  const _onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    if (onCellClicked) {
      onCellClicked(cell, event)
    }
  }, [onCellClicked])

  useEffect(() => {
    if (onSort) {
      onSort(dataGridArgs.sort)
    }
  }, [onSort, dataGridArgs.sort])

  return (
    <>
      <ThemedDataGrid
        {...rest}
        {...dataGridArgs}
        ref={ref}
        width="100%"
        smoothScrollX={true}
        smoothScrollY={true}
        rowMarkers="number"
        getCellsForSelection={true}
        onCellClicked={onCellClicked ? _onCellClicked : undefined}
        getRowThemeOverride={getRowThemeOverride}
        onCellContextMenu={onCellContextMenu}
        gridSelection={gridSelection}/>
      {isOpen &&
        renderLayer(
          <div {...layerProps}>
            <ContextMenu
              {...contextMenu}
              onItemClick={onContextMenuItemClick}/>
          </div>
        )
      }
    </>
  );
});

export type ShiftDataGridContextMenuItem = "EDIT" | "NEW";

type ContextMenuProps = {
  data: ShiftEntity[];
  onItemClick: (item: ShiftDataGridContextMenuItem, data: ShiftEntity) => void
}

const ContextMenu = (props: ContextMenuProps) => {
  const {data, onItemClick} = props;

  const navigate = useNavigate()
  const {t} = useTranslation(["common"]);

  if (data.length === 0) {
    return undefined
  }

  const item = data[0]

  return (
    <Menu shadow="md" width={200} opened={true}>
      <Menu.Dropdown>
        <Menu.Item
          icon={<IconEye size={14}/>}
          onClick={() => navigate(`/shifts/profile?id=${item.id}`)}>
          {t("common.action.view", "View")}
        </Menu.Item>
        <HasAccess permissions={["shift_edit"]}>
          <Menu.Item
            icon={<IconEdit size={14}/>}
            onClick={() => onItemClick("EDIT", item)}>
            {t("common.action.edit")}
          </Menu.Item>
        </HasAccess>
        <HasAccess permissions={["shift_create"]}>
          <Menu.Item
            icon={<IconCopy size={14}/>}
            onClick={() => onItemClick("NEW", item)}>
            {t("common.action.new")}
          </Menu.Item>
        </HasAccess>
      </Menu.Dropdown>
    </Menu>
  );
};
